import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import {
  ShieldCheck,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Webhook,
} from 'lucide-react';

interface WebhookDiagnostic {
  topic: string;
  uri: string;
  apiVersion: string;
  apiVersionSupported: boolean;
}

interface StoreDiagnostic {
  shopDomain: string;
  shopName: string | null;
  ianaTimezone: string | null;
  currencyCode: string | null;
  taxesIncluded: boolean | null;
  queriedApiVersion: string;
  configuredApiVersion: string;
  configuredApiVersionSupported: boolean;
  supportedApiVersions: string[];
  latestSupportedApiVersion: string | null;
  grantedScopes: string[];
  missingScopes: string[];
  webhooks: WebhookDiagnostic[];
  webhookApiVersions: string[];
  orderProbe?: {
    windowDays: number;
    sampledOrders: number;
    hasMore: boolean;
    sourceNames: string[];
  };
}

interface FleetRow {
  storeId: number;
  ok: boolean;
  diagnostic?: StoreDiagnostic;
  error?: string;
}

interface FleetResponse {
  queriedApiVersion: string;
  storeCount: number;
  scopeHealthyCount: number;
  failedCount: number;
  results: FleetRow[];
}

const DIAGNOSTIC_URL = '/api/v1/admin/shopify/diagnostic';

/**
 * Version choices offered in the picker. "Configured" runs against whatever the
 * adapter is pinned to; the others let you rehearse an upgrade read-only, before
 * changing any deployed configuration.
 */
const VERSION_CHOICES = ['configured', '2026-07', '2026-04', '2026-01', '2025-10'] as const;

export function ShopifyDiagnosticPage() {
  const [apiVersion, setApiVersion] = useState<string>('configured');
  const [expanded, setExpanded] = useState<number | null>(null);

  const query = useQuery({
    queryKey: ['shopify-diagnostic', apiVersion],
    queryFn: async ({ signal }) => {
      const qs = apiVersion === 'configured' ? '' : `?apiVersion=${apiVersion}`;
      const res = await api.get<FleetResponse>(`${DIAGNOSTIC_URL}${qs}`, { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Diagnostic failed');
      return res.data;
    },
    // Each row is a live call out to Shopify — refetch on demand, not on a timer.
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
  });

  const rows = query.data?.results ?? [];
  const scopeGaps = rows.filter((r) => r.ok && (r.diagnostic?.missingScopes.length ?? 0) > 0);
  const versionGaps = rows.filter(
    (r) => r.ok && r.diagnostic && !r.diagnostic.configuredApiVersionSupported,
  );
  const noWebhooks = rows.filter((r) => r.ok && r.diagnostic?.webhooks.length === 0);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 flex-wrap">
        <ShieldCheck className="w-5 h-5 text-gray-500 dark:text-gray-400" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Shopify Install Diagnostic
        </h2>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          Granted scopes, API version support, and webhook registration — read live from Shopify.
        </span>

        <div className="ml-auto flex items-center gap-2">
          <label className="text-xs text-gray-500 dark:text-gray-400">Run against</label>
          <select
            value={apiVersion}
            onChange={(e) => setApiVersion(e.target.value)}
            className="text-sm border border-gray-300 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          >
            {VERSION_CHOICES.map((v) => (
              <option key={v} value={v}>
                {v === 'configured' ? 'Configured version' : v}
              </option>
            ))}
          </select>
          <button
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
            className="flex items-center gap-1.5 text-sm px-3 py-1 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 text-gray-700 dark:text-gray-200"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${query.isFetching ? 'animate-spin' : ''}`} />
            Run
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Active stores" value={query.data?.storeCount ?? '—'} />
        <Tile
          label="Missing scopes"
          value={scopeGaps.length}
          tone={scopeGaps.length > 0 ? 'warn' : 'ok'}
        />
        <Tile
          label="Unsupported API version"
          value={versionGaps.length}
          tone={versionGaps.length > 0 ? 'warn' : 'ok'}
        />
        <Tile
          label="Unreachable"
          value={query.data?.failedCount ?? 0}
          tone={(query.data?.failedCount ?? 0) > 0 ? 'bad' : 'ok'}
        />
      </div>

      {scopeGaps.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/40 px-4 py-3">
          <AlertTriangle className="w-4 h-4 text-amber-700 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900 dark:text-amber-200">
            <span className="font-medium">
              {scopeGaps.length} store{scopeGaps.length === 1 ? '' : 's'} missing required scopes.
            </span>{' '}
            Scopes are granted per install and are never widened retroactively — these stores need
            the merchant to re-consent before an API version bump.
          </div>
        </div>
      )}

      {noWebhooks.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/60 px-4 py-3">
          <Webhook className="w-4 h-4 text-gray-500 dark:text-gray-400 mt-0.5 shrink-0" />
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <span className="font-medium">
              {noWebhooks.length} store{noWebhooks.length === 1 ? '' : 's'} have no webhooks
              registered.
            </span>{' '}
            Those stores are fed entirely by scheduled pulls.
          </div>
        </div>
      )}

      <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
        {query.isLoading ? (
          <div className="p-6 text-sm text-gray-500 dark:text-gray-400">
            Querying Shopify for each store…
          </div>
        ) : query.isError ? (
          <div className="p-6 text-sm text-red-600 dark:text-red-400">
            {(query.error as Error).message}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-gray-500 dark:text-gray-400">
            No active Shopify connections.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800">
                <tr>
                  {['Store', 'Shop', 'API version', 'Missing scopes', 'Webhooks', ''].map((h) => (
                    <th
                      key={h}
                      className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <StoreRow
                    key={row.storeId}
                    row={row}
                    expanded={expanded === row.storeId}
                    onToggle={() => setExpanded(expanded === row.storeId ? null : row.storeId)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Tile({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: number | string;
  tone?: 'neutral' | 'ok' | 'warn' | 'bad';
}) {
  const toneClass =
    tone === 'warn'
      ? 'text-amber-700 dark:text-amber-400'
      : tone === 'bad'
        ? 'text-red-600 dark:text-red-400'
        : tone === 'ok'
          ? 'text-emerald-600 dark:text-emerald-400'
          : 'text-gray-900 dark:text-gray-100';
  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 px-4 py-3">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`text-2xl font-semibold mt-0.5 ${toneClass}`}>{value}</div>
    </div>
  );
}

function StoreRow({
  row,
  expanded,
  onToggle,
}: {
  row: FleetRow;
  expanded: boolean;
  onToggle: () => void;
}) {
  if (!row.ok || !row.diagnostic) {
    return (
      <tr className="border-b border-gray-100 dark:border-gray-800/60">
        <td className="px-4 py-2 font-medium text-gray-900 dark:text-gray-100">{row.storeId}</td>
        <td className="px-4 py-2 text-red-600 dark:text-red-400" colSpan={5}>
          <span className="inline-flex items-center gap-1.5">
            <XCircle className="w-3.5 h-3.5" />
            {row.error ?? 'Unreachable'}
          </span>
        </td>
      </tr>
    );
  }

  const d = row.diagnostic;
  const scopesOk = d.missingScopes.length === 0;

  return (
    <>
      <tr className="border-b border-gray-100 dark:border-gray-800/60 hover:bg-gray-50 dark:hover:bg-gray-800/40">
        <td className="px-4 py-2 font-medium text-gray-900 dark:text-gray-100">{row.storeId}</td>
        <td className="px-4 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
          {d.shopDomain}
        </td>
        <td className="px-4 py-2 whitespace-nowrap">
          <span
            className={
              d.configuredApiVersionSupported
                ? 'text-gray-700 dark:text-gray-300'
                : 'text-amber-700 dark:text-amber-400 font-medium'
            }
          >
            {d.configuredApiVersion}
          </span>
          {!d.configuredApiVersionSupported && (
            <span className="text-xs text-amber-600 dark:text-amber-500 ml-1.5">
              unsupported · latest {d.latestSupportedApiVersion ?? '?'}
            </span>
          )}
        </td>
        <td className="px-4 py-2">
          {scopesOk ? (
            <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="w-3.5 h-3.5" />
              none
            </span>
          ) : (
            <span className="text-amber-700 dark:text-amber-400 font-mono text-xs">
              {d.missingScopes.join(', ')}
            </span>
          )}
        </td>
        <td className="px-4 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
          {d.webhooks.length === 0 ? (
            <span className="text-gray-400 dark:text-gray-500">none</span>
          ) : (
            <>
              {d.webhooks.length}
              <span className="text-xs text-gray-500 dark:text-gray-400 ml-1.5">
                @ {d.webhookApiVersions.join(', ')}
              </span>
            </>
          )}
        </td>
        <td className="px-4 py-2 text-right">
          <button
            onClick={onToggle}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            {expanded ? 'Hide' : 'Details'}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50 dark:bg-gray-950/60 border-b border-gray-100 dark:border-gray-800/60">
          <td colSpan={6} className="px-4 py-3">
            <div className="grid md:grid-cols-2 gap-4 text-xs">
              <div>
                <div className="font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Granted scopes ({d.grantedScopes.length})
                </div>
                <div className="font-mono text-gray-600 dark:text-gray-400 leading-relaxed">
                  {d.grantedScopes.join(', ') || '—'}
                </div>
                <div className="font-medium text-gray-700 dark:text-gray-200 mt-3 mb-1">Shop</div>
                <div className="text-gray-600 dark:text-gray-400">
                  {d.shopName ?? '—'} · {d.currencyCode ?? '—'} · {d.ianaTimezone ?? '—'} · tax
                  {d.taxesIncluded ? '-inclusive' : '-exclusive'} pricing
                </div>
              </div>
              <div>
                <div className="font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Webhook subscriptions
                </div>
                {d.webhooks.length === 0 ? (
                  <div className="text-gray-500 dark:text-gray-400">
                    None registered — this store is fed only by scheduled pulls.
                  </div>
                ) : (
                  <ul className="space-y-1">
                    {d.webhooks.map((w) => (
                      <li key={w.topic + w.uri} className="text-gray-600 dark:text-gray-400">
                        <span className="font-mono">{w.topic}</span>
                        <span
                          className={
                            w.apiVersionSupported
                              ? ' text-gray-500 dark:text-gray-500'
                              : ' text-amber-600 dark:text-amber-400 font-medium'
                          }
                        >
                          {' '}
                          @{w.apiVersion}
                          {!w.apiVersionSupported && ' (unsupported)'}
                        </span>
                        <div className="truncate text-gray-400 dark:text-gray-600">{w.uri}</div>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="font-medium text-gray-700 dark:text-gray-200 mt-3 mb-1">
                  Supported API versions
                </div>
                <div className="font-mono text-gray-600 dark:text-gray-400">
                  {d.supportedApiVersions.join(', ') || '—'}
                </div>
                {d.orderProbe && (
                  <>
                    <div className="font-medium text-gray-700 dark:text-gray-200 mt-3 mb-1">
                      Order probe · last {d.orderProbe.windowDays}d at {d.queriedApiVersion}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">
                      {d.orderProbe.sampledOrders} order
                      {d.orderProbe.sampledOrders === 1 ? '' : 's'} sampled
                      {d.orderProbe.hasMore ? ' (more exist)' : ''} · sources:{' '}
                      <span className="font-mono">
                        {d.orderProbe.sourceNames.join(', ') || 'none'}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
